import { useState } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"
import { ChevronDown, ChevronUp } from "lucide-react"

export default function RoiCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [purchasePrice, setPurchasePrice] = useState(250000)
  const [downPayment, setDownPayment] = useState(50000)
  const [closingCosts, setClosingCosts] = useState(5000)
  const [annualCashFlow, setAnnualCashFlow] = useState(2400)
  const [appreciationRate, setAppreciationRate] = useState(3)
  const [principalPaydown, setPrincipalPaydown] = useState(2000)
  const [holdingPeriod, setHoldingPeriod] = useState(10)

  const [showTable, setShowTable] = useState(false)

  // Calculations
  const totalCashInvested = downPayment + closingCosts
  const totalCashFlow = annualCashFlow * holdingPeriod
  const totalEquity = principalPaydown * holdingPeriod
  const appreciationGain = purchasePrice * (Math.pow(1 + appreciationRate / 100, holdingPeriod) - 1)
  
  const totalReturn = totalCashFlow + totalEquity + appreciationGain
  const roi = totalCashInvested > 0 ? (totalReturn / totalCashInvested) * 100 : 0
  const annualizedROI = holdingPeriod > 0 && totalCashInvested > 0 ? (Math.pow(1 + roi / 100, 1 / holdingPeriod) - 1) * 100 : 0

  // S&P 500 comparison at 10.5% historical avg
  const sp500Return = totalCashInvested * (Math.pow(1.105, holdingPeriod) - 1)

  // Projection Table array
  const projectionArray = Array.from({ length: holdingPeriod }, (_, i) => {
    const year = i + 1
    const cf = annualCashFlow * year
    const eq = principalPaydown * year
    const app = purchasePrice * (Math.pow(1 + appreciationRate / 100, year) - 1)
    const yTotal = cf + eq + app
    const yRoi = totalCashInvested > 0 ? (yTotal / totalCashInvested) * 100 : 0
    return { year, cf, eq, app, yTotal, yRoi }
  })

  return (
    <ToolShell>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-8">
        
        {/* Inputs */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-slate-50 border border-slate-200 p-5 rounded-xl space-y-4">
            <h4 className="font-semibold text-slate-800 border-b border-slate-200 pb-2">Initial Investment</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Purchase Price</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={purchasePrice || ''} onChange={(e) => setPurchasePrice(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Down Payment</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={downPayment || ''} onChange={(e) => setDownPayment(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Closing Costs</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={closingCosts || ''} onChange={(e) => setClosingCosts(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 p-5 rounded-xl space-y-4">
            <h4 className="font-semibold text-slate-800 border-b border-slate-200 pb-2">Returns & Horizon</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Net Annual Cash Flow</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={annualCashFlow || ''} onChange={(e) => setAnnualCashFlow(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Annual Principal Paydown</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={principalPaydown || ''} onChange={(e) => setPrincipalPaydown(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Appreciation Rate: {appreciationRate}%</label>
              <input type="range" min="0" max="15" step="0.5" value={appreciationRate} onChange={(e) => setAppreciationRate(Number(e.target.value))} className="w-full accent-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Holding Period: {holdingPeriod} Years</label>
              <input type="range" min="1" max="30" step="1" value={holdingPeriod} onChange={(e) => setHoldingPeriod(Number(e.target.value))} className="w-full accent-primary" />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl text-white shadow-xl flex flex-col sm:flex-row gap-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2"></div>
            
            <div className="flex-1 relative z-10">
              <span className="text-slate-400 text-sm font-medium block mb-1">Return on Investment (Total)</span>
              <span className="text-5xl font-black">{roi.toFixed(1)}%</span>
              <div className="mt-4 pt-4 border-t border-white/10">
                <span className="text-slate-400 text-sm font-medium block mb-1">Total Cash Invested</span>
                <span className="text-2xl font-bold">{currency}{totalCashInvested.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="flex-1 relative z-10 border-t sm:border-t-0 sm:border-l border-white/10 pt-4 sm:pt-0 sm:pl-6">
              <span className="text-slate-400 text-sm font-medium block mb-1">Annualized ROI</span>
              <span className="text-5xl font-black text-primary-300">{annualizedROI.toFixed(2)}%</span>
              <div className="mt-4 pt-4 border-t border-white/10">
                <span className="text-slate-400 text-sm font-medium block mb-1">Total Wealth Generated</span>
                <span className="text-2xl font-bold text-green-400">+{currency}{totalReturn.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h4 className="font-semibold text-slate-900 mb-4">The 3 Sources of Wealth</h4>
            <div className="space-y-4">
              {[
                { label: "Cash Flow (Income)", val: totalCashFlow, color: "bg-blue-400" },
                { label: "Equity (Principal Paydown)", val: totalEquity, color: "bg-teal-400" },
                { label: "Appreciation (Market Growth)", val: appreciationGain, color: "bg-purple-400" }
              ].map((item, idx) => {
                const pct = totalReturn > 0 ? (item.val / totalReturn) * 100 : 0
                return (
                  <div key={idx} className="flex flex-col gap-1.5 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">{item.label}</span>
                      <span className="font-bold text-slate-900">{currency}{item.val.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-slate-400 px-1">/</span> {pct.toFixed(0)}%</span>
                    </div>
                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 rounded-bl-lg">BENCHMARK</div>
            <h4 className="font-semibold text-slate-900 mb-2">Vs. The S&P 500</h4>
            <p className="text-sm text-slate-600">
              If you put your {currency}{totalCashInvested.toLocaleString()} down payment into an S&P 500 index fund returning a historical average 10.5% yearly, you would have made <strong className="text-slate-900">{currency}{sp500Return.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong> over {holdingPeriod} years.
            </p>
            <p className="text-sm font-medium text-slate-900 mt-2">
              Real estate {totalReturn > sp500Return ? 'outperformed' : 'underperformed'} the index by {currency}{Math.abs(totalReturn - sp500Return).toLocaleString(undefined, { maximumFractionDigits: 0 })}.
            </p>
          </div>

          {/* Projection Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
             <button 
                onClick={() => setShowTable(!showTable)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <h3 className="font-semibold text-slate-900">Year-by-Year Projection</h3>
                {showTable ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>
              {showTable && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left whitespace-nowrap">
                    <thead className="bg-white border-y border-slate-200">
                      <tr>
                        <th className="px-4 py-3 font-medium text-slate-500">Year</th>
                        <th className="px-4 py-3 font-medium text-slate-500">Cash Flow</th>
                        <th className="px-4 py-3 font-medium text-slate-500">Equity</th>
                        <th className="px-4 py-3 font-medium text-slate-500">Appreciation</th>
                        <th className="px-4 py-3 font-semibold text-primary">Cum. ROI</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {projectionArray.map((row) => (
                        <tr key={row.year} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">Year {row.year}</td>
                          <td className="px-4 py-3 text-slate-600">{currency}{row.cf.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-slate-600">{currency}{row.eq.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-slate-600">{currency}{row.app.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{row.yRoi.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 pb-10">
        <FormulaBox 
          title="Return on Investment Formulas"
          formula={`Cash Invested = Down Payment + Closing Costs\n\nTotal Return = (Annual Cash Flow × Years) + (Annual Paydown × Years) + (Price × ((1 + Appr Pct)^Years - 1))\n\nTotal ROI = (Total Return / Cash Invested) × 100\nAnnualized ROI = (((1 + Total ROI/100)^(1/Years)) - 1) × 100`} 
        />
        <CTABox icon={tool.icon} heading="Access powerful portfolio analytics" body={tool.ctaText} ctaLabel="Join for Free" ctaHref="/register" />
        <ToolFAQ 
          items={[
            { q: "Why is Real Estate ROI often higher than stocks?", a: "Leverage. You are making 3% appreciation on a {currency}250k asset, but you only inputted {currency}50k to control it. That 3% jump on {currency}250k is {currency}7,500, which represents a massive 15% return on your actual {currency}50k cash deposited." },
            { q: "What is Annualized ROI vs Total ROI?", a: "Total ROI just measures the lump sum gain compared to your deposit (e.g. 200%). Annualized ROI smooths that gain over the number of years to give you an equivalent yearly interest rate (CAGR), making it easier to compare against 6% savings accounts." }
          ]} 
        />
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
